import { compressImage } from '../../inspections/image_compression';

export function initializeFileHandler(container, fileField, previewsContainer, selectionList) {
  if (container.dataset.handlersInitialized === 'true') {
    return;
  }
  
  // Mark as initialized
  container.dataset.handlersInitialized = 'true';
  
  // Make the container clickable to trigger file input
  container.style.cursor = 'pointer';
  
  // Add click handler with a flag to prevent multiple triggers
  let isClicking = false;
  
  // Create event handlers and store references
  container._uploadHandler = function(e) {
    if (isClicking) {
      return; // Prevent multiple triggers
    }
    
    // Only trigger if clicking on the upload message area, not on previews or other elements
    if (e.target.closest('.upload-message') && !e.target.closest('.file-preview')) {
      e.preventDefault();
      e.stopPropagation();
      isClicking = true;
      
      // Use setTimeout to prevent immediate re-triggering
      setTimeout(() => {
        fileField.click();
        isClicking = false;
      }, 10);
    }
  };
  
  container._dragOverHandler = function(e) {
    e.preventDefault();
    e.stopPropagation();
    container.style.borderColor = '#5065F6';
    container.style.backgroundColor = '#F4F7FB';
  };
  
  container._dragLeaveHandler = function(e) {
    e.preventDefault();
    e.stopPropagation();
    container.style.borderColor = '#D0D7E2';
    container.style.backgroundColor = '#F9FAFB';
  };
  
  container._dropHandler = async function(e) {
    e.preventDefault();
    e.stopPropagation();
    container.style.borderColor = '#D0D7E2';
    container.style.backgroundColor = '#F9FAFB';
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const _files = filterAcceptedFiles(Array.from(files), fileField);

      // parallel compression
      const processedFiles = await Promise.all(
        _files.map(async file => {
          if (file.type.startsWith('image/')) {
            const compressedFile = await compressImage(file);
            return compressedFile;
          } else {
            return file;
          }
        })
      );

      processedFiles.forEach(processedFile => {
        handleFileSelection(processedFile, fileField, previewsContainer, selectionList);
      });
    }
    updateFileInputValue(fileField, selectionList);
  };
  
  // Add event listeners
  container.addEventListener('mousedown', container._uploadHandler);
  container.addEventListener('dragover', container._dragOverHandler);
  container.addEventListener('dragleave', container._dragLeaveHandler);
  container.addEventListener('drop', container._dropHandler);
  
  // Handle file input change
  fileField.addEventListener('change', async function(e) {
    const files = e.target.files;
    if (files.length > 0) {
      const _files = filterAcceptedFiles(Array.from(files), fileField);

      // parallel compression
      const processedFiles = await Promise.all(
        _files.map(async file => {
          if (file.type.startsWith('image/')) {
            const compressedFile = await compressImage(file);
            return compressedFile;
          } else {
            return file;
          }
        })
      );

      processedFiles.forEach(processedFile => {
        handleFileSelection(processedFile, fileField, previewsContainer, selectionList);
      });
    }
    updateFileInputValue(fileField, selectionList);
  });
}

// Dropped files bypass the input's `accept` attribute, so enforce it manually.
function filterAcceptedFiles(files, fileField) {
  const accept = (fileField.getAttribute('accept') || '').trim();
  if (!accept) return files;

  const patterns = accept.split(',').map(p => p.trim().toLowerCase()).filter(Boolean);
  const accepted = files.filter(file => {
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();
    return patterns.some(pattern => {
      if (pattern.startsWith('.')) return name.endsWith(pattern);
      if (pattern.endsWith('/*')) return type.startsWith(pattern.slice(0, -1));
      return type === pattern;
    });
  });

  const rejected = files.length - accepted.length;
  if (rejected > 0) {
    alert(`${rejected} file(s) skipped: unsupported file type.${accept === 'application/pdf' ? ' Only PDF files are accepted.' : ''}`);
  }
  return accepted;
}

export function removeFile(e, selectionList) {
  if (!e.target.matches('i.fa.fa-times')) return;

  const previewElement = e.target.closest('.file-preview');
  const filePreviewsDom = e.target.closest('.file-previews.m-0');
  // Scope to the containing form so duplicate IDs across tab pane and modal
  // don't cause getElementById to return the wrong element.
  const scopeRoot = e.target.closest('form') || document;
  const fileField = scopeRoot.querySelector('#' + e.target.dataset.fileFieldId);
  const index = Array.from(filePreviewsDom.children).indexOf(previewElement);

  removeFileFromFileList(index, fileField, e.target, filePreviewsDom, selectionList);

  const container = previewElement.parentNode.closest('.upload-form.file-upload-area');
  previewElement.remove();

  // Re-enable the upload area
  if (container) {
    container.classList.remove('has-file');
    container.style.cursor = 'pointer';
    
    // Show the upload message again
    const uploadMessage = container.querySelector('.upload-message');
    if (uploadMessage) {
      uploadMessage.style.display = 'block';
    }
    
    // Re-add event listeners if they exist
    if (container._uploadHandler) {
      container.addEventListener('mousedown', container._uploadHandler);
      container.addEventListener('dragover', container._dragOverHandler);
      container.addEventListener('dragleave', container._dragLeaveHandler);
      container.addEventListener('drop', container._dropHandler);
    }
  }
};

function handleFileSelection(file, fileField, previewsContainer, selectionList) {
  // Validate file size
  const minimumFileSize = 200 * 1024 * 1024; // 200MB
  if (file.size > minimumFileSize) {
    alert("File size is larger than 200MB");
    return;
  }
  
  if (!fileField.multiple) {
    selectionList.length = 0;
    previewsContainer.querySelectorAll('.file-preview').forEach(prev => {
      const icon = prev.querySelector('[data-existing-file="true"]');
      if (icon) {
        prev.style.textDecoration = 'line-through';
        prev.style.opacity = '0.5';
        const targetId = icon.getAttribute('data-hidden-target');
        const checkbox = document.getElementById(targetId);
        if (checkbox) checkbox.checked = true;
      } else {
        prev.remove();
      }
    });
  }

  const preview = document.createElement('div');
  preview.className = 'file-preview d-flex align-items-center justify-content-between gap-2';
  preview.innerHTML = `
    <a href="/" target="_blank" class="wrap-link text-truncate" style="font-size: 12px; flex: 1;"> ${file.name}</a>
    <div class="file-remove">
      <i class="fa fa-times" style="color: #F33838; cursor: pointer;" data-file-field-id=${fileField.id}></i>
    </div>
  `;
  preview.addEventListener('click', (e) => removeFile(e, selectionList));
  
  selectionList.push(file);
  previewsContainer.appendChild(preview);

  updateRequireClass(fileField);
}

function removeFileFromFileList(index, fileField, target, filePreviewsDom, selectionList) {
  const dataTransfer = new DataTransfer();

  // handling edit — scope checkbox lookup to the same .file-previews container
  // to avoid matching the identically-named checkbox in the tab pane when the
  // modal is also in the DOM.
  if (target.dataset.existingFile === 'true') {
    const destroyFlag = filePreviewsDom.querySelector('#' + target.dataset.hiddenTarget);
    if (destroyFlag) destroyFlag.checked = 1;
    return
  }

  selectionList.splice(index, 1);
  selectionList.map(file => dataTransfer.items.add(file));

  fileField.files = dataTransfer.files;
  console.log(selectionList);
}

function updateFileInputValue(fileInput, selectionList) {
  const dataTransfer = new DataTransfer();
  selectionList.map(file => {
    dataTransfer.items.add(file);
  });
  fileInput.files = dataTransfer.files;
}

function updateRequireClass(field) {
  let uploadForm = $(field).closest('div.upload-form.file-upload-area');

  if(validFileField(field)) {
    uploadForm.removeClass('is-invalid error-field');
    uploadForm.siblings('.error-message').remove();
  } else {
    uploadForm.addClass('is-invalid error-field');
    uploadForm.after(errorElement);
  }
}

function validFileField(fileField) {
  let uploadFormConatiner = fileField.closest('div[data-target=upload-form]');
  let filePreviewDom = $(uploadFormConatiner).find('div[data-preview]');

  return filePreviewDom.find('div.file-preview').length > 0;
}
